import { ConfigType } from 'dayjs';
import { getDayjs, MAX_TIMESTAMP, MIN_TIMESTAMP } from '../../common/utils';

export type DateRange = [ConfigType?, ConfigType?];
export type EntityWithDateRange<KeysWithDateRange extends string> = {
  id: string | number;
} & {
  [K in KeysWithDateRange]: DateRange | null;
};

type RangeCache = Map<string | number, (DateRange | null)[]>;

export const isRangesOverlapping = <K extends string>(
  entities: EntityWithDateRange<K>[],
  key: K,
) => {
  const rangeCache: RangeCache = new Map();
  const invalidIndexes: Set<number> = new Set();

  for (const [index, entity] of entities.entries()) {
    const range = entity[key];

    if (rangeCache.has(entity.id)) {
      for (const cachedRange of rangeCache.get(entity.id) || []) {
        const cachedIndex = Array.from(rangeCache.keys()).indexOf(entity.id);
        if (isDateRangesOverlapping(range, cachedRange)) {
          invalidIndexes.add(cachedIndex);
          invalidIndexes.add(index);
        }
      }
    } else {
      rangeCache.set(entity.id, []);
    }

    rangeCache.get(entity.id)?.push(range);
  }

  return Array.from(invalidIndexes);
};

export const isDateRangesOverlapping = (
  a: [ConfigType?, ConfigType?] | null,
  b: [ConfigType?, ConfigType?] | null,
) => {
  // Returning early because if either range is missing both start and end terminals (they are falsy),
  // then they surely overlap since unterminated range translates to -Infinity to Infinity and
  // that overlaps with everything.
  if (a?.every((x) => !x) || b?.every((x) => !x)) {
    return true;
  }

  const A = toDayjs(a);
  const B = toDayjs(b);

  return (
    A.start.isBetween(B.start, B.end, 'day', '[]') ||
    A.end.isBetween(B.start, B.end, 'day', '[]') ||
    B.start.isBetween(A.start, A.end, 'day', '[]') ||
    B.end.isBetween(A.start, A.end, 'day', '[]')
  );
};

const toDayjs = (range: [ConfigType?, ConfigType?] | null) => ({
  start: range?.[0]
    ? getDayjs(range[0])!.startOf('day')
    : getDayjs(MIN_TIMESTAMP)!,
  end: range?.[1] ? getDayjs(range[1])!.endOf('day') : getDayjs(MAX_TIMESTAMP)!,
});

export const isDateRangeContained = (
  a: [ConfigType?, ConfigType?] | null,
  b: [ConfigType?, ConfigType?] | null,
) => {
  // Returning early because if range is missing both start and end terminals (they are falsy),
  // then any given date is surely in range since unterminated range translates to -Infinity to Infinity and
  // that includes all dates.
  if (!a || a?.every((x) => !x)) {
    return true;
  }

  const A = toDayjs(a);
  const B = toDayjs(b);

  return (
    B.start.isBetween(A.start, A.end, 'day', '[]') &&
    B.end.isBetween(A.start, A.end, 'day', '[]')
  );
};

export function isOutsidePeriodRange<K extends string>(
  validPeriod: DateRange,
  entities: EntityWithDateRange<K>[],
  key: K,
) {
  const invalidIndexes = [];
  const [validFrom, validTo] = validPeriod;

  for (const [index, entity] of entities.entries()) {
    const [from, to] = entity[key] || [];
    if (from && validFrom && from < validFrom) {
      invalidIndexes.push(index);
    }

    if (to && validTo && to > validTo) {
      invalidIndexes.push(index);
    }
  }

  return invalidIndexes;
}
